import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Common/button';

import * as yaml from 'js-yaml';

interface SourceType {
  id: string;
  code: string;
  nom: string;
  description: string;
  poids: number;
  froid_chaud: 'froid' | 'chaud';
}

interface SourceTypesFormProps {
  initialContent?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

export function SourceTypesForm({ initialContent, onSave, onCancel }: SourceTypesFormProps) {
  const { t } = useTranslation();
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as { source_types: Omit<SourceType, 'id'>[] };
        if (parsed?.source_types && Array.isArray(parsed.source_types)) {
          const typesWithIds = parsed.source_types.map((type, index) => ({
            ...type,
            id: `source-${Date.now()}-${index}`,
          }));
          setSourceTypes(typesWithIds);
        }
      } catch (error) {
        console.error('Error parsing source types YAML:', error);
      }
    }
  }, [initialContent]);

  const handleAddSourceType = () => {
    const newType: SourceType = {
      id: `source-${Date.now()}`,
      code: '',
      nom: '',
      description: '',
      poids: 1.0,
      froid_chaud: 'froid',
    };
    setSourceTypes([newType, ...sourceTypes]);
    setExpandedTypes(new Set([...expandedTypes, newType.id]));
  };

  const handleUpdateSourceType = (id: string, field: keyof Omit<SourceType, 'id'>, value: string | number) => {
    setSourceTypes(sourceTypes.map(type =>
      type.id === id ? { ...type, [field]: value } : type
    ));
  };

  const handleDeleteSourceType = (id: string) => {
    setSourceTypes(sourceTypes.filter(type => type.id !== id));
    const newExpanded = new Set(expandedTypes);
    newExpanded.delete(id);
    setExpandedTypes(newExpanded);
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedTypes(newExpanded);
  };

  const handleSave = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const typesData = sourceTypes.map(({ id: _id, ...type }) => type);

    const yamlContent = yaml.dump(
      { source_types: typesData },
      {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        sortKeys: false,
      }
    );

    onSave(yamlContent);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <p className="text-sm text-muted-foreground">
            {t('admin.configForms.sourceTypes.intro')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('admin.configForms.sourceTypes.hint')}
          </p>
        </div>
        <Button
          onClick={handleAddSourceType}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('admin.configForms.sourceTypes.addType')}
        </Button>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {sourceTypes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('admin.configForms.sourceTypes.empty')}
          </div>
        ) : (
          sourceTypes.map((type) => (
            <div
              key={type.id}
              className="border border-border rounded-lg overflow-hidden bg-card"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                onClick={() => toggleExpanded(type.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {type.code || 'new_source'}
                    </code>
                    <span className="font-medium truncate">
                      {type.nom || t('admin.configForms.shared.untitled')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      type.froid_chaud === 'froid'
                        ? 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300'
                        : 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300'
                    }`}>
                      {type.froid_chaud === 'froid'
                        ? t('admin.configForms.sourceTypes.coldBadge')
                        : t('admin.configForms.sourceTypes.hotBadge')}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {type.poids.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSourceType(type.id);
                    }}
                    className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  {expandedTypes.has(type.id) ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>

              {expandedTypes.has(type.id) && (
                <div className="p-4 pt-0 space-y-4 border-t border-border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('admin.configForms.shared.code')}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={type.code}
                        onChange={(e) => handleUpdateSourceType(type.id, 'code', e.target.value)}
                        placeholder={t('admin.configForms.sourceTypes.codePlaceholder')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('admin.configForms.shared.snakeCaseHint')}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('global.name')}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={type.nom}
                        onChange={(e) => handleUpdateSourceType(type.id, 'nom', e.target.value)}
                        placeholder={t('admin.configForms.sourceTypes.namePlaceholder')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('global.description')}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={type.description}
                      onChange={(e) => handleUpdateSourceType(type.id, 'description', e.target.value)}
                      placeholder={t('admin.configForms.sourceTypes.descriptionPlaceholder')}
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('admin.configForms.sourceTypes.weight')}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={type.poids}
                        onChange={(e) => handleUpdateSourceType(type.id, 'poids', parseFloat(e.target.value) || 1.0)}
                        min="0.8"
                        max="1.5"
                        step="0.05"
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('admin.configForms.sourceTypes.weightHint')}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('admin.configForms.sourceTypes.classification')}
                        <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={type.froid_chaud}
                        onChange={(e) => handleUpdateSourceType(type.id, 'froid_chaud', e.target.value as 'froid' | 'chaud')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                      >
                        <option value="froid">{t('admin.configForms.sourceTypes.coldOption')}</option>
                        <option value="chaud">{t('admin.configForms.sourceTypes.hotOption')}</option>
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('admin.configForms.sourceTypes.classificationHint')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button onClick={onCancel} variant="outline">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
